import { MaintenanceWindowSchedulerView } from '../view/MaintenanceWindowSchedulerView';
import { templateMaintenanceWindowScheduler } from '../view/template';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { MaintenanceWindowSchedulerWebComponent } from '..';
import { FrequencyOptions, RecurrenceOptions } from '../../../types/common/schedulerTypes';
import { MaintenanceWindowSchedulerSchema } from '../types';
import { FormGroupHelper } from '../../../utils/maintenanceWindow/FormGroupHelper';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { TimePickerEvents } from '../../../ui/gzTimePicker/types';

export class MaintenanceWindowSchedulerController extends BaseWebComponentController {
  public component: MaintenanceWindowSchedulerWebComponent;
  public view: MaintenanceWindowSchedulerView;
  public viewConstructor: typeof MaintenanceWindowSchedulerView;

  constructor(component: MaintenanceWindowSchedulerWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: MaintenanceWindowSchedulerView,
      viewTemplate: templateMaintenanceWindowScheduler,
    });
  }

  public computeEventListeners() {
    return [
      {
        element: this.view.dom.$recurrenceFormField.Input,
        event: 'selectionChanged',
        callback: this.toggleRecurrenceOptions,
      },
      {
        element: this.view.dom.$monthlyFrequencyFormField.Input,
        event: 'selectionChanged',
        callback: this.onSelectionChangedMonthlyFrequency,
      },
      {
        element: this.view.dom.$recurrenceFormField.Input,
        event: 'triggerRevalidation',
        callback: this.restoreSchedulerState,
      },
      {
        element: this.view.dom.$betweenStartFormField.Input,
        event: TimePickerEvents.CHANGE,
        callback: this.triggerFormValidation,
      },
    ];
  }

  private triggerFormValidation() {
    this.view.formGroup.validate();
  }

  private toggleRecurrenceOptions(ev: WebComponentEvent): void {
    switch (ev.detail.value) {
      case RecurrenceOptions.IMMEDIATELY.toString():
        // hide daily and weekly containers
        this.displayWeeklyOptions(false);
        this.displayMonthlyOptions(false);
        this.displayCommonOptions(false);
        break;
      case RecurrenceOptions.WEEKLY.toString():
        // show appliesEveryWeeks / hide appliesEveryMonths
        this.displayWeeklyOptions(true);
        this.displayMonthlyOptions(false);
        this.displayCommonOptions(true);
        break;
      case RecurrenceOptions.MONTHLY.toString():
        // hide appliesEveryWeeks / show appliesEveryMonths
        this.displayWeeklyOptions(false);
        this.displayMonthlyOptions(true);
        this.displayCommonOptions(true);
        break;
      default:
        return;
    }
    this.view.formGroup.validate();
  }

  private restoreSchedulerState(): void {
    const recurrenceValue = this.view.dom.$recurrenceFormField.Input.getFormValue();
    this.toggleRecurrenceOptions({ detail: { value: recurrenceValue } } as WebComponentEvent);
  }

  private onSelectionChangedMonthlyFrequency(ev: WebComponentEvent): void {
    switch (ev.detail.value) {
      case FrequencyOptions.DAY.toString():
        // show textfield / hide day dropdown
        toggleVisibility(this.view.dom.$monthlyFrequencyDayFormField, false);
        toggleVisibility(this.view.dom.$monthlyFrequencyDayNumberFormField, true);
        FormGroupHelper.setDisabledFormFields(true, [this.view.dom.$monthlyFrequencyDayFormField]);
        FormGroupHelper.setDisabledFormFields(false, [this.view.dom.$monthlyFrequencyDayNumberFormField]);
        break;
      case FrequencyOptions.FIRST.toString():
      case FrequencyOptions.SECOND.toString():
      case FrequencyOptions.THIRD.toString():
      case FrequencyOptions.FOURTH.toString():
      case FrequencyOptions.LAST.toString():
        // show textfield / hide day dropdown
        toggleVisibility(this.view.dom.$monthlyFrequencyDayFormField, true);
        toggleVisibility(this.view.dom.$monthlyFrequencyDayNumberFormField, false);
        FormGroupHelper.setDisabledFormFields(false, [this.view.dom.$monthlyFrequencyDayFormField]);
        FormGroupHelper.setDisabledFormFields(true, [this.view.dom.$monthlyFrequencyDayNumberFormField]);
        break;
      default:
        toggleVisibility(this.view.dom.$monthlyFrequencyDayFormField, false);
        toggleVisibility(this.view.dom.$monthlyFrequencyDayNumberFormField, false);
        FormGroupHelper.setDisabledFormFields(true, [this.view.dom.$monthlyFrequencyDayFormField]);
        FormGroupHelper.setDisabledFormFields(true, [this.view.dom.$monthlyFrequencyDayNumberFormField]);
    }
  }

  private displayWeeklyOptions(display: boolean): void {
    toggleVisibility(this.view.dom.$recurrenceWeeklyOptionsContainer, display);
    FormGroupHelper.setDisabledFormFields(!display, [
      this.view.dom.$appliesEveryWeeksFormField,
      this.view.dom.$weeklyOnFormField,
    ]);
  }

  private displayMonthlyOptions(display: boolean): void {
    toggleVisibility(this.view.dom.$recurrenceMonthlyOptionsContainer, display);
    if (display) {
      FormGroupHelper.setDisabledFormFields(false, [
        this.view.dom.$appliesEveryMonthsFormField,
        this.view.dom.$monthlyFrequencyFormField,
      ]);

      // ensure fields that are hidden should be disabled
      FormGroupHelper.setDisabledFormFields(this.view.dom.$monthlyFrequencyDayFormField.classList.contains('hidden'), [
        this.view.dom.$monthlyFrequencyDayFormField,
      ]);
      FormGroupHelper.setDisabledFormFields(
        this.view.dom.$monthlyFrequencyDayNumberFormField.classList.contains('hidden'),
        [this.view.dom.$monthlyFrequencyDayNumberFormField]
      );
    } else {
      FormGroupHelper.setDisabledFormFields(true, [
        this.view.dom.$appliesEveryMonthsFormField,
        this.view.dom.$monthlyFrequencyFormField,
        this.view.dom.$monthlyFrequencyDayFormField,
        this.view.dom.$monthlyFrequencyDayNumberFormField,
      ]);
    }
  }

  private displayCommonOptions(display: boolean): void {
    toggleVisibility(this.view.dom.$commonOptionsContainer, display);
    FormGroupHelper.setDisabledFormFields(!display, [
      this.view.dom.$startingWithFormField,
      this.view.dom.$betweenStartFormField,
      this.view.dom.$betweenEndFormField,
    ]);
  }

  async getDataForView(): Promise<{
    viewData: MaintenanceWindowSchedulerSchema | null;
    hasImmediatelyOption: boolean;
    disabled: boolean;
  }> {
    return {
      viewData: this.component.viewData,
      disabled: this.component.disabled,
      hasImmediatelyOption: this.component.hasImmediatelyOption,
    };
  }
}
